import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getRequestsByCategory } from "../request-orders.service";
import { formatDate } from "shared/helpers/utils";
import useModal from "shared/hooks/useModel";
import NewRequestModal from "./newRequestModal";
import ChatDrawer from "./chatView";
import DxfGrid from "shared/component/dxf-grid/dxf-grid";
import { Column } from "devextreme-react/data-grid";
import "../requestOrders.scss";

const STAGES = [
  { value: "all", key: "all" },
  { value: "pending", key: "stage-pending" },
  { value: "opened", key: "stage-opened" },
  { value: "in review", key: "stage-review" },
  { value: "success", key: "stage-success" },
  { value: "failed", key: "stage-failed" },
];

const stageBadgeClass = (stage) => {
  switch (stage?.toLowerCase()) {
    case "pending": return "badge-pending";
    case "opened": return "badge-opened";
    case "in review": return "badge-review";
    case "success": return "badge-success";
    case "failed": return "badge-failed";
    default: return "badge-opened";
  }
};

const RequestList = () => {
  const { t } = useTranslation();
  const { categoryId } = useParams();
  const location = useLocation();
  const categoryName = location.state?.categoryName || t("request-orders.requests");
  const [showNewRequest, openNewRequest, closeNewRequest] = useModal(false);
  const [showChat, openChat, closeChat] = useModal(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [requests, setRequests] = useState([]);
  const lastJsonRef = useRef("");

  const fetchRequests = useCallback(async () => {
    try {
      const data = await getRequestsByCategory(Number(categoryId), 0);
      const json = JSON.stringify(data);
      if (json !== lastJsonRef.current) {
        lastJsonRef.current = json;
        setRequests(data);
      }
    } catch {
      // handled by client
    }
  }, [categoryId]);

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 2000);
    return () => clearInterval(interval);
  }, [fetchRequests]);

  const filteredRequests = stageFilter === "all"
    ? requests
    : requests.filter((r) => r.stage?.toLowerCase() === stageFilter);

  const handleRequestCreated = () => {
    closeNewRequest();
    fetchRequests();
  };

  const handleOpenChat = (req) => {
    setSelectedRequest(req);
    openChat();
  };

  return (
    <div className="myBooking request-page">
      <div className="container">
        <div className="order-header">
          <div className="header">
            <div className="page-name">
              <h1 className="title">{categoryName}</h1>
            </div>
          </div>
        </div>
        <div className="request-toolbar">
          <div className="toolbar-left">
            <button className="btn-add" onClick={openNewRequest}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t("request-orders.new")}
            </button>
          </div>
          <div className="toolbar-right">
            <div className="stage-chips">
              <svg className="filter-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              {STAGES.map((s) => (
                <button
                  key={s.value}
                  className={`stage-chip ${s.value !== "all" ? `chip-${s.value.replace(" ", "-")}` : ""} ${stageFilter === s.value ? "active" : ""}`}
                  onClick={() => setStageFilter(s.value)}
                >
                  {s.value !== "all" && <span className={`chip-dot dot-${s.value.replace(" ", "-")}`} />}
                  {s.value === "all" ? t("common.select-all") : t(`request-orders.${s.key}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="dataTable request-grid">
          <DxfGrid
            dataSource={filteredRequests}
            rowAlternationEnabled={true}
            onRowClick={(e) => handleOpenChat(e.data)}
            scrolling={{ mode: "standard" }}
            loadPanel={{ enabled: false }}
          >
            <Column dataField="id" caption="#" width={60} alignment="center" />
            <Column dataField="title" caption={t("request-orders.title")} />
            <Column dataField="description" caption={t("request-orders.description")} />
            <Column
              dataField="stage"
              caption={t("request-orders.stage")}
              width={130}
              alignment="center"
              cellRender={(cell) => (
                <span className={`stage-badge ${stageBadgeClass(cell.value)}`}>
                  {cell.value}
                </span>
              )}
            />
            <Column
              dataField="creationDate"
              caption={t("request-orders.createdAt")}
              width={140}
              alignment="center"
              cellRender={(c) => formatDate(c.value)}
            />
            <Column
              dataField="updatedDate"
              caption={t("request-orders.updatedAt")}
              width={140}
              alignment="center"
              cellRender={(c) => formatDate(c.value)}
            />
            <Column
              caption=""
              width={50}
              alignment="center"
              fixed={true}
              fixedPosition="right"
              cellRender={(cell) => (
                <button
                  className="btn-chat-icon"
                  onClick={(e) => { e.stopPropagation(); handleOpenChat(cell.data); }}
                  title={t("request-orders.comments")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                </button>
              )}
            />
          </DxfGrid>
        </div>
      </div>

      <NewRequestModal
        show={showNewRequest}
        onHide={closeNewRequest}
        categoryId={Number(categoryId)}
        onCreated={handleRequestCreated}
      />

      <ChatDrawer
        show={showChat}
        onHide={closeChat}
        categoryId={Number(categoryId)}
        requestId={selectedRequest?.id}
        requestTitle={selectedRequest?.title}
      />
    </div>
  );
};

export default RequestList;
